<template>
  <PoderiLayout
    active-item="home"
    hide-navbar-title
  >
    <!-- SRH01: striscia di accesso rapido sopra elenco/dettaglio aziende (solo con flag misura21) -->
    <Misura21HomeBlock v-if="!loading && store.isAuthenticated && farmCentersCount > 0" compact />

    <!-- Loading state -->

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ t('farmList.loading') }}</p>
    </div>

    <!-- Single farm center: show detail -->
    <FarmCenterDetail 
      v-else-if="farmCentersCount === 1" 
      :key="farmCenters[0]?.id_farm_center"
      :farm-center-id="farmCenters[0]?.id_farm_center"
      :breadcrumbs="[{ label: 'Home' }]"
    />

    <!-- Multiple farm centers: show list -->
    <FarmCenterList
      v-else-if="farmCentersCount > 1"
      :farm-centers="farmCenters"
      :plots="plots"
      :seasons="seasons"
      :loading="loading"
      :error="error"
      :has-farm-centers="hasFarmCenters"
      :farm-centers-count="farmCentersCount"
      :breadcrumbs="[{ label: 'Home' }]"
      :can-create="canCreateFarm"
      @create="openCreateForm"
      @edit="editFarm"
      @delete="confirmDelete"
      @view="viewFarmDetail"
      @retry="retryLoad"
    />

    <!-- No farm centers: empty state -->
    <div v-else class="empty-state">
      <AeCard class="empty-card">
        <div v-if="store.isAuthenticated" class="welcome-content">
          <AeAlert type="success" :title="t('homeView.loggedInTitle')">
            {{ t('homeView.userLabel', { name: store.getUserName }) }}
          </AeAlert>
          <!-- Utenti SRH01 senza aziende proprie: il blocco SRH01 sostituisce l'invito a creare
               un'azienda (che resta come azione secondaria, tranne per org_external/UCI). -->
          <template v-if="m21.links.length">
            <Misura21HomeBlock />
            <div v-if="canCreateFarm" class="m21-create-farm">
              <AeButton variant="ghost" size="small" @click="openCreateForm">
                <AeIcon name="Plus" :size="14" /> {{ t('farmList.createFirstBtn') }}
              </AeButton>
            </div>
          </template>
          <template v-else-if="canCreateFarm">
            <p>{{ t('farmList.noFarms') }}</p>
            <p>{{ t('farmList.createFirst') }}</p>
            <AeButton variant="primary" @click="openCreateForm">
              {{ t('farmList.createFirstBtn') }}
            </AeButton>
          </template>
        </div>
        
        <div v-else class="welcome-guest">

          
          <div class="app-intro">
            <div class="intro-icon">
              <img v-if="skinStore.logo" :src="skinStore.logo" :alt="skinStore.appName" class="intro-logo" />
              <AeIcon v-else name="sprout" :size="64" />
            </div>
            <div class="intro-content">
              <h3>{{ skinStore.appName }}</h3>
              <div class="intro-text" v-html="skinStore.description"></div>
            </div>
          </div>

          <div class="features-grid">
            <div v-for="(feat, i) in skinStore.features" :key="i" class="feature-item">
              <div class="feature-icon">
                <AeIcon :name="feat.icon" :size="32" />
              </div>
              <h4>{{ feat.title }}</h4>
              <p>{{ feat.text }}</p>
            </div>
          </div>

          <div class="auth-section">
            <AeAuthentication :store="store" />
          </div>
        </div> 
      </AeCard>
    </div>

    <!-- Modals -->
    <FarmCenterFormModal
      v-model="showForm"
      :farm-center-id="editingFarmId"
      @submit="handleFormSubmit"
    />

    <AeModal v-model="showDeleteConfirm" :title="t('farmDetail.confirmDeleteTitle')" size="small">
      <p>{{ t('farmDetail.deleteFarmConfirm') }}</p>
      <p v-if="farmToDelete" class="confirmation-text">
        <strong>{{ farmToDelete.name }}</strong>
      </p>
      <p class="helper-text">{{ t('farmDetail.cannotUndo') }}</p>

      <template #footer>
        <div class="modal-actions">
          <AeButton variant="secondary" @click="showDeleteConfirm = false">
            {{ t('farmDetail.cancel') }}
          </AeButton>
          <AeButton variant="danger" @click="deleteFarm">
            {{ t('farmDetail.delete') }}
          </AeButton>
        </div>
      </template>
    </AeModal>
  </PoderiLayout>
</template>

<style scoped>
.m21-create-farm {
  display: flex;
  justify-content: center;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid var(--border-color, #eee);
  border-top-color: var(--primary-color, #4299e1);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.welcome-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4, 16px);
}

.navigation-actions {
  display: flex;
  gap: 1rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.confirmation-text {
  color: var(--danger-color, #e53e3e);
  margin: 1rem 0;
  font-weight: 600;
}

.helper-text {
  font-size: 0.85rem;
  color: #999;
}

.welcome-guest {
  padding: 2rem 1rem;
  max-width: 900px;
  margin: 0 auto;
}

.app-intro {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
}

.intro-icon {
  flex: 0 0 50%;
  max-width: 50%;
  color: var(--primary-color, #4299e1);
}

.intro-logo {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.intro-content {
  flex: 1;
}

.app-intro h3 {
  font-size: 1.75rem;
  margin-bottom: 1rem;
  color: var(--text-primary, #2d3748);
  text-align: left;
}

.intro-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--text-secondary, #718096);
  text-align: left;
}

@media (max-width: 600px) {
  .app-intro {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .intro-icon {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .app-intro h3 {
    font-size: 1.3rem;
    text-align: center;
  }

  .intro-text {
    text-align: center;
  }
}

@media (min-width: 1200px) {
  .app-intro {
    gap: 4rem;
  }
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.feature-item {
  text-align: center;
  padding: 1.5rem;
  background: var(--bg-secondary, #f7fafc);
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.feature-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  margin-bottom: 0.75rem;
  color: var(--primary-color, #4299e1);
}

.feature-item h4 {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary, #2d3748);
}

.feature-item p {
  font-size: 0.9rem;
  color: var(--text-secondary, #718096);
  line-height: 1.5;
}

.auth-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

/* Forza la scritta del tasto login visibile anche su mobile */
.auth-section :deep(.ae-login-button-text) {
  display: inline !important;
}

</style>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '../stores/app.js'
import { useDbStore } from '../stores/dbStore.js'
import { usePoderiStore } from '../stores/poderiStore'
import { useSkinStore } from '../stores/skinStore.js'
import { AeButton, AeCard, AeModal, AeAlert, AeIcon } from '@aevue/components'
import PoderiLayout from '../layouts/PoderiLayout.vue'
import FarmCenterDetail from '../components/FarmCenterDetail.vue'
import FarmCenterList from '../components/FarmCenterList.vue'
import FarmCenterFormModal from '../components/FarmCenterFormModal.vue'
import Misura21HomeBlock from '../modules/misura21/components/Misura21HomeBlock.vue'
import { getMisura21Access } from '../modules/misura21/misura21Access.js'

const router = useRouter()
const { t } = useI18n()
const store = useAppStore()
const dbStore = useDbStore()
const poderiStore = usePoderiStore()
const skinStore = useSkinStore()

// SRH01: blocco di accesso per ruolo in Home; org_external (UCI) non crea aziende.
const m21 = computed(() => getMisura21Access(store))
const canCreateFarm = computed(() => m21.value.canCreateFarm)

const showForm = ref(false)
const showDeleteConfirm = ref(false)
const editingFarmId = ref(null)
const farmToDelete = ref(null)

// Local loading state - non usare poderiStore.loading perché viene modificato anche da FarmCenterDetail
const loading = ref(false)
const error = ref(null)
const farmCenters = ref([])
const hasFarmCenters = computed(() => (farmCenters.value || []).length > 0)
const farmCentersCount = computed(() => (farmCenters.value || []).length)
const plots = ref([])
const seasons = ref([])

// ⚠️ RACE CONDITION NOTA — docs/local-persistence.md §3.4
//
// Scenario: utente già autenticato, `dbStore.init()` rileva schema cambiato o sync
// interrotto → setta isReady=true E needsResync=true nello stesso tick.
// isAppReady diventa true → Home monta → loadData() legge IndexedDB vuota → "0 farm centers".
// Subito dopo, il watcher needsResync in App.vue avvia attemptRecovery() → isReady=false
// → loading spinner → recovery → isReady=true → Home rimonta e carica correttamente.
//
// Il problema è la finestra tra isReady=true (init) e isReady=false (recovery inizia).
// In più: checkSyncStatus() NON viene chiamato all'avvio — dati stale restano visibili
// finché non scatta un trigger (online/visibility dopo 5min/timer 15min).
//
// PROTEZIONE: se dopo loadData() farmCenters è vuota ma l'utente è autenticato,
// registriamo un watch su dbStore.syncInProgress: quando la sync termina, ricarichiamo.
// Un retry ritardato copre la finestra di race più breve (recovery già in corso al mount).


async function retryLoad() {
  await loadData()
}

let _retryScheduled = false
// Limite massimo di retry per evitare loop infinito quando l'utente ha
// legittimamente zero aziende (non è una race condition, è lo stato reale).
const MAX_EMPTY_RETRIES = 2
let _emptyRetryCount = 0

async function loadData() {
  loading.value = true
  error.value = null
  _retryScheduled = false
  try {
    farmCenters.value = (await poderiStore.fetchFarmCenters()) || []
    const [plotsData, seasonsData] = await Promise.all([
      poderiStore.fetchAllPlots(),
      poderiStore.fetchAllSeasons()
    ])
    plots.value = plotsData || []
    seasons.value = seasonsData || []
  } catch (e) {
    error.value = e.message
    console.error('Error loading data:', e)
  } finally {
    loading.value = false
  }

  if (farmCenters.value.length > 0) {
    _emptyRetryCount = 0
    return
  }

  // Protezione race condition §3.4 local-persistence.md:
  // Se subito dopo il mount i dati sono vuoti ma l'utente è autenticato,
  // significa che il recovery o il sync iniziale erano ancora in corso.
  // Un numero limitato di retry copre la finestra di race più comune,
  // senza ripetere all'infinito se l'utente ha davvero zero aziende.
  if (store.isAuthenticated && !_retryScheduled && _emptyRetryCount < MAX_EMPTY_RETRIES) {
    _retryScheduled = true
    _emptyRetryCount++
    setTimeout(async () => {
      if (farmCenters.value.length === 0) {
        console.log(`Home: farmCenters vuoti, retry loadData dopo attesa sync... (${_emptyRetryCount}/${MAX_EMPTY_RETRIES})`)
        await loadData()
      }
    }, 3000)
  }
}

// Protezione race condition §3.4: se una sync era in corso al mount e termina
// con farmCenters ancora vuota, ricarica i dati (entro il limite di retry).
watch(() => dbStore.syncInProgress, async (inProgress, wasInProgress) => {
  if (
    wasInProgress && !inProgress &&
    farmCenters.value.length === 0 && store.isAuthenticated &&
    _emptyRetryCount < MAX_EMPTY_RETRIES
  ) {
    console.log('Home: sync completata con farmCenters vuoti, ricarico...')
    await loadData()
  }
})

function openCreateForm() {
  editingFarmId.value = null
  showForm.value = true
}

function editFarm(farm) {
  editingFarmId.value = farm.id_farm_center
  showForm.value = true
}

function closeForm() {
  showForm.value = false
  editingFarmId.value = null
}

async function handleFormSubmit() {
  closeForm()
  await loadData()
}

function confirmDelete(farm) {
  farmToDelete.value = farm
  showDeleteConfirm.value = true
}

async function deleteFarm() {
  if (!farmToDelete.value) return
  const result = await poderiStore.deleteFarmCenter(farmToDelete.value.id_farm_center)
  if (result.ok) {
    showDeleteConfirm.value = false
    farmToDelete.value = null
    await loadData()
  } else {
    alert(result.message || t('farmDetail.errorDeleteGeneric'))
  }
}

function viewFarmDetail(farm) {
  router.push(`/poderi/${farm.id_farm_center}`)
}

onMounted(async () => {
  if (store.isAuthenticated) {
    await loadData()
  }
})
</script>

